import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaEnvelope, FaPhone, FaArrowRight, FaGraduationCap, FaHeart, FaHandshake, FaBook } from 'react-icons/fa';

interface VolunteerFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  gender: string;
  interest: string;
  availability: string;
  physicalChanged: string;
  message: string;
}

const Volunteer: React.FC = () => {
  const [formData, setFormData] = useState<VolunteerFormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    gender: '',
    interest: '',
    availability: '',
    physicalChanged: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Use Formspree alternative - Send email directly using FormSubmit.co
      const response = await fetch('https://formsubmit.co/ajax/hello@mylvtrust.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          gender: formData.gender,
          interest: formData.interest,
          availability: formData.availability,
          physicalChanged: formData.physicalChanged,
          message: formData.message,
          subject: 'Volunteer Application'
        })
      });

      const result = await response.json();

      if (result.success) {
        // Show success popup
        setShowSuccess(true);

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          gender: '',
          interest: '',
          availability: '',
          physicalChanged: '',
          message: ''
        });

        console.log('Volunteer form submitted successfully');
      } else {
        throw new Error('Failed to submit form');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your application. Please try again or email us directly at hello@mylvtrust.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const volunteerReasons = [
    {
      icon: <FaUsers className="w-8 h-8 text-primary" />,
      title: "Make a meaningful impact",
      description: "Help transform lives and communities through your contributions."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-primary transform rotate-180" />,
      title: "Develop new skills",
      description: "Gain valuable experience and enhance your professional skills."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-primary" />,
      title: "Build connections",
      description: "Meet like-minded individuals who share your passion for service."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-primary transform rotate-180" />,
      title: "Personal fulfillment",
      description: "Experience the joy of giving back and making a difference."
    }
  ];

  const eligibilityOptions = [
    { label: "Students", icon: <FaGraduationCap className="w-8 h-8 text-blue-600" /> },
    { label: "Doctors", icon: <FaHeart className="w-8 h-8 text-red-600" /> },
    { label: "Teachers", icon: <FaBook className="w-8 h-8 text-green-600" /> },
    {
      label: "Homemakers", icon: (
        <div className="relative w-8 h-8 flex items-center justify-center">
          {/* Roof */}
          <div className="w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[10px] border-b-pink-600 absolute top-1"></div>
          {/* House Body */}
          <div className="w-5 h-5 bg-pink-600 absolute top-4 rounded-sm"></div>
          {/* Doorway */}
          <div className="w-2 h-3 bg-white absolute top-5 rounded-b-full"></div>
        </div>
      )
    },
    { label: "Corporate Volunteers", icon: <FaUsers className="w-8 h-8 text-purple-600" /> },
    { label: "Other Trust", icon: <FaHandshake className="w-8 h-8 text-orange-600" /> }
  ];

  return (
    <div className="relative w-full">
      {/* Background Image with proper scrolling behavior */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/backend log/volbg.png')",
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          pointerEvents: 'none'
        }}
      />

      {/* Main Content */}
      <div className="relative w-full min-h-screen">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 md:bg-black/20 backdrop-blur-[2px] md:backdrop-blur-sm"></div>
          <div className="relative z-10 w-full">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-2xl">
                  Become a <span className="text-primary">Volunteer</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-white mb-8 max-w-3xl mx-auto drop-shadow-xl font-medium px-4">
                  Join us in making a difference. Your time and skills can help transform lives and communities. We welcome volunteers from all backgrounds and skill sets.
                </p>
                <div className="flex justify-center px-4">
                  <a
                    href="#volunteer-form"
                    className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-secondary to-secondary-dark text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-sm md:text-base"
                  >
                    Join as a Volunteer
                    <FaArrowRight className="ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Volunteer Section */}
        <section className="py-12 md:py-16 bg-white/80 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 drop-shadow-md">Why Volunteer With Us</h2>
              <div className="w-20 h-1 bg-primary mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {volunteerReasons.map((reason, index) => (
                <div
                  key={index}
                  className="group bg-white p-5 md:p-6 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden hover:-translate-y-1"
                  style={{
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '0px',
                    borderBottomLeftRadius: '0px',
                    borderBottomRightRadius: '20px'
                  }}
                >
                  {/* Hover border effect */}
                  <div
                    className="absolute inset-0 border-2 border-transparent group-hover:border-[#8A7B2E] 
                           transition-all duration-300 pointer-events-none"
                    style={{
                      borderTopLeftRadius: '20px',
                      borderTopRightRadius: '0px',
                      borderBottomLeftRadius: '0px',
                      borderBottomRightRadius: '20px'
                    }}
                  ></div>

                  {/* Number Badge (replacing icon) */}
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-amber-100/80 flex items-center justify-center mb-4 mx-auto 
                           group-hover:scale-105 transition-transform duration-300">
                    <span className="text-[#8A7B2E] font-bold text-xl md:text-2xl">0{index + 1}</span>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 text-center">{reason.title}</h3>
                    <p className="text-sm md:text-base text-gray-600 text-center">{reason.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Can Volunteer Section */}
        <section className="py-12 md:py-16 bg-white/80 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 drop-shadow-md">Who Can Volunteer</h2>
              <div className="w-20 h-1 bg-primary mx-auto mb-6 md:mb-8"></div>
              <p className="text-sm md:text-base text-gray-600 mb-8 px-4">
                No strict qualifications required — passion to serve is key.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8 justify-items-center">
                {eligibilityOptions.map((item, index) => (
                  <div key={index} className="flex flex-col items-center group">
                    <div className="relative mb-4">
                      {/* Gradient background circle */}
                      <div className="absolute inset-0 w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 
                                   group-hover:from-primary/30 group-hover:to-secondary/30 transition-all duration-500 
                                   group-hover:scale-110 blur-sm"></div>

                      {/* Icon container */}
                      <div className="relative w-20 h-20 rounded-full bg-white border-2 border-primary/20 flex items-center justify-center
                                   shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105 group-hover:border-primary/40">
                        <div className="bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent">
                          {item.icon}
                        </div>
                      </div>
                    </div>

                    {/* Label with enhanced styling */}
                    <div className="text-center">
                      <span className="block font-semibold text-gray-800 group-hover:text-primary transition-colors duration-300">
                        {item.label}
                      </span>
                      <div className="h-0.5 w-0 bg-gradient-to-r from-primary to-secondary mt-1 
                                  group-hover:w-full transition-all duration-300"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Volunteer Registration Form */}
        <section id="volunteer-form" className="py-12 md:py-16 bg-white/90 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-premium overflow-hidden">
              <div className="md:flex">
                <div className="p-6 md:p-8 w-full">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Volunteer Registration</h2>
                    <div className="w-20 h-1 bg-primary mx-auto mb-6"></div>
                    <p className="text-sm md:text-base text-gray-600">Fill out the form below to join our volunteer team</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
                          placeholder="Your full name"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
                          placeholder="your.email@example.com"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone/WhatsApp *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
                          placeholder="+91 98765 43210"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                        <select
                          id="gender"
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none"
                          required>
                          <option value="" className="text-gray-400">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="transgender">Transgender</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="physicalChanged" className="block text-sm font-medium text-gray-700 mb-1">Physical Changed Person *</label>
                        <select
                          id="physicalChanged"
                          name="physicalChanged"
                          value={formData.physicalChanged}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none"
                          required
                        >
                          <option value="">Select option</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-1">Area of Interest *</label>
                        <select
                          id="interest"
                          name="interest"
                          value={formData.interest}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none"
                          required
                        >
                          <option value="">Select an area</option>
                          <option value="teaching">Education</option>
                          <option value="healthcare">Healthcare</option>
                          <option value="event">Event Management</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="col-span-1 md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
                          placeholder="Your complete address"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message / Motivation *</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
                        placeholder="Tell us why you want to volunteer with us..."
                        required
                      ></textarea>
                    </div>

                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="terms"
                          name="terms"
                          type="checkbox"
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          required
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="terms" className="text-gray-600">
                          I agree to the volunteer terms and acknowledge the{' '}
                          <Link to="/privacy-policy" className="text-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                            Privacy Policy &amp; DPDPA Notice
                          </Link>
                          . Your data is processed solely for volunteer onboarding &amp; program coordination in accordance with the DPDP Act, 2023.
                        </label>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="text-center">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className={`w-full md:w-auto px-8 py-4 bg-gradient-to-r from-secondary to-secondary-dark text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300 transform hover:scale-105 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                            }`}
                        >
                          {isSubmitting ? 'Submitting...' : 'Register as Volunteer'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center bg-white/50 p-6 md:p-8 rounded-2xl border border-primary/10 backdrop-blur-sm">
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 md:mb-6">Have questions about volunteering?</h3>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 md:gap-8 text-gray-700">
                <div className="flex items-center group">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3 group-hover:bg-primary/20 transition-colors">
                    <FaPhone className="text-primary" />
                  </div>
                  <span className="font-medium">+91 9942019000</span>
                </div>
                <div className="flex items-center group">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3 group-hover:bg-primary/20 transition-colors">
                    <FaEnvelope className="text-primary" />
                  </div>
                  <span className="font-medium">hello@mylvtrust.com</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Popup */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
            <div className="bg-white rounded-lg p-8 max-w-md mx-4 transform transition-all duration-300 animate-fadeIn">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Application Submitted!</h3>
                <p className="text-gray-600 mb-6">
                  Your message has been successfully sent. We'll get back to you within 24 hours.
                </p>

                <button
                  onClick={() => setShowSuccess(false)}
                  className="bg-primary hover:bg-primary-dark text-white font-medium py-2 px-6 rounded-lg transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Volunteer;
